import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Download, Plus, X, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { useAllAgreements, useSegments, useSaveSegment, useDeleteSegment, DEPT_LABEL, type AgrReportRow } from "./api";

// ============================================================================
// REPORT-01 — the report builder (first entity: Agreements, DEC-24 v1 scope).
// Pick filters + a group-by + an aggregate over the agreement book, see it as a
// chart + table, export CSV. The query the user builds here is exactly the shape
// SEG-01 will save as a reusable Segment. Runner is client-side (fetch once,
// filter/group in memory) — the book is small and it matches the other reports.
// ============================================================================

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

type GroupKey = "status" | "plan" | "dept" | "zone" | "type" | "expireYear" | "signedYear";
const GROUPS: [GroupKey, string][] = [
  ["status", "Status"], ["plan", "Plan term"], ["dept", "Department"], ["zone", "Zone (city)"],
  ["type", "Agreement type"], ["expireYear", "Expiry year"], ["signedYear", "Signed year"],
];

type AggKey = "count" | "sum" | "avg";
const AGGS: [AggKey, string][] = [["count", "Count"], ["sum", "Total value"], ["avg", "Avg value"]];

type FieldType = "enum" | "number" | "date";
interface FilterField { key: string; label: string; type: FieldType; options?: string[] }
const STATUS_OPTS = ["active", "expiring", "expired", "inactive"];
const PLAN_OPTS = ["1 Year", "2 Year", "3 Year", "5 Year"];
const FILTER_FIELDS: FilterField[] = [
  { key: "status", label: "Status", type: "enum", options: STATUS_OPTS },
  { key: "plan", label: "Plan Term", type: "enum", options: PLAN_OPTS },
  { key: "dept", label: "Department", type: "enum", options: Object.values(DEPT_LABEL) },
  { key: "zone", label: "Zone (city)", type: "enum" },
  { key: "value", label: "Contract Value", type: "number" },
  { key: "expires", label: "Expiry Date", type: "date" },
  { key: "signed", label: "Signed Date", type: "date" },
];
function fieldType(key: string): FieldType {
  return FILTER_FIELDS.find((f) => f.key === key)?.type ?? "enum";
}
function opsFor(type: FieldType): [string, string][] {
  if (type === "number") return [["gte", "≥"], ["lte", "≤"]];
  if (type === "date") return [["after", "on/after"], ["before", "on/before"]];
  return [["is", "is"]];
}

interface FilterRow { id: number; field: string; op: string; value: string }

function rowValue(r: AgrReportRow, field: string): string | number | null {
  switch (field) {
    case "status": return r.status;
    case "plan": return r.plan;
    case "dept": return r.dept;
    case "zone": return r.zone;
    case "type": return r.type;
    case "value": return r.value;
    case "expires": return r.expires;
    case "signed": return r.signed;
    default: return null;
  }
}
function passes(r: AgrReportRow, f: FilterRow): boolean {
  if (!f.value) return true;
  const v = rowValue(r, f.field);
  switch (f.op) {
    case "is": return String(v ?? "") === f.value;
    case "gte": return Number(v ?? 0) >= Number(f.value);
    case "lte": return Number(v ?? 0) <= Number(f.value);
    case "after": return !!v && String(v) >= f.value;
    case "before": return !!v && String(v) <= f.value;
    default: return true;
  }
}
function groupValue(r: AgrReportRow, by: GroupKey): string {
  const v = r[by];
  return v == null || v === "" ? "—" : String(v);
}

function money(v: number): string {
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function StyledSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="h-9 cursor-pointer appearance-none rounded-lg border border-hairline bg-white pl-3 pr-9 text-sm font-medium text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30">
        {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>{children}</label>;
}

interface OutRow { key: string; label: string; count: number; sum: number; metric: number }

export function CustomReport() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"summary" | "list">("summary");
  const [groupBy, setGroupBy] = useState<GroupKey>("status");
  const [agg, setAgg] = useState<AggKey>("count");
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const { data, isLoading, isError } = useAllAgreements();
  const { data: segments } = useSegments("agreement");
  const saveSeg = useSaveSegment();
  const delSeg = useDeleteSegment();
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [savingName, setSavingName] = useState<string | null>(null);
  const zoneOptions = useMemo(
    () => Array.from(new Set((data ?? []).map((r) => r.zone).filter((z) => z && z !== "—"))).sort(),
    [data],
  );

  let nextId = 0;
  const addFilter = () => setFilters((fs) => [...fs, { id: Date.now() + nextId++, field: "status", op: "is", value: "" }]);
  const removeFilter = (id: number) => setFilters((fs) => fs.filter((f) => f.id !== id));
  const updateFilter = (id: number, patch: Partial<FilterRow>) =>
    setFilters((fs) => fs.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  function loadSegment(id: string) {
    if (!id) { setLoadedId(null); return; }
    const seg = (segments ?? []).find((s) => s.id === id);
    if (!seg) return;
    const def = seg.definition as { groupBy?: GroupKey; agg?: AggKey; filters?: FilterRow[] };
    setGroupBy(def.groupBy ?? "status");
    setAgg(def.agg ?? "count");
    setFilters(def.filters ?? []);
    setLoadedId(id);
  }
  function doSaveSegment() {
    const name = (savingName ?? "").trim();
    if (!name) return;
    saveSeg.mutate(
      { name, entity: "agreement", definition: { groupBy, agg, filters } },
      { onSuccess: () => setSavingName(null) },
    );
  }

  const { rows, matched, totalValue, members } = useMemo(() => {
    const all = data ?? [];
    const kept = all.filter((r) => filters.every((f) => passes(r, f)));
    const map = new Map<string, { count: number; sum: number }>();
    for (const r of kept) {
      const k = groupValue(r, groupBy);
      const cur = map.get(k) ?? { count: 0, sum: 0 };
      cur.count += 1;
      cur.sum += r.value;
      map.set(k, cur);
    }
    let rows: OutRow[] = Array.from(map.entries()).map(([key, v]) => ({
      key,
      label: key,
      count: v.count,
      sum: v.sum,
      metric: agg === "count" ? v.count : agg === "sum" ? v.sum : v.count ? v.sum / v.count : 0,
    }));
    rows = groupBy === "expireYear" || groupBy === "signedYear"
      ? rows.sort((a, b) => a.key.localeCompare(b.key))
      : rows.sort((a, b) => b.metric - a.metric);
    return { rows, matched: kept.length, totalValue: kept.reduce((s, r) => s + r.value, 0), members: kept };
  }, [data, filters, groupBy, agg]);

  const max = rows.reduce((m, g) => Math.max(m, g.metric), 0);
  const aggLabel = AGGS.find(([k]) => k === agg)?.[1] ?? "Count";
  const groupLabel = GROUPS.find(([k]) => k === groupBy)?.[1] ?? "Group";
  const fmtMetric = (n: number) => (agg === "count" ? n.toLocaleString() : money(n));

  function exportCsv() {
    const esc = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
    const lines: string[] = [];
    if (viewMode === "list") {
      lines.push("Sedgwick Heating — Agreement members");
      if (filters.length) lines.push(`Filters,"${filters.filter((f) => f.value).map((f) => `${f.field} ${f.op} ${f.value}`).join("; ")}"`);
      lines.push(`Members,${matched}`);
      lines.push("");
      lines.push("Customer,Custno,Status,Plan,Zone,Value,Expires");
      for (const m of members) {
        lines.push([esc(m.customer), m.custno, m.status, esc(m.plan), esc(m.zone), String(m.value), m.expires ?? ""].join(","));
      }
    } else {
      lines.push("Sedgwick Heating — Custom agreements report");
      lines.push(`Grouped by,${groupLabel}`);
      lines.push(`Measure,${aggLabel}`);
      if (filters.length) lines.push(`Filters,"${filters.filter((f) => f.value).map((f) => `${f.field} ${f.op} ${f.value}`).join("; ")}"`);
      lines.push(`Agreements matched,${matched}`);
      lines.push("");
      lines.push(`${groupLabel},Count,Total value`);
      for (const g of rows) {
        lines.push(`${esc(g.label)},${g.count},${g.sum}`);
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `agreements_${viewMode === "list" ? "members" : `${groupBy}_${agg}`}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  return (
    <div>
      {/* Controls */}
      <Card className="mb-5 p-4">
        {/* SEG-01 — saved segments: load a saved view or save the current one. */}
        <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-hairline pb-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Saved segments</span>
          {(segments ?? []).length > 0 ? (
            <StyledSelect
              value={loadedId ?? ""}
              onChange={loadSegment}
              options={[["", "Load a segment…"], ...(segments ?? []).map((s) => [s.id, s.name] as [string, string])]}
            />
          ) : (
            <span className="text-xs text-slate-400">none yet — build a view and save it</span>
          )}
          {savingName === null ? (
            <button type="button" onClick={() => setSavingName("")}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 hover:text-brand-700">
              <Plus className="h-3.5 w-3.5" /> Save current view
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <input autoFocus value={savingName} placeholder="Segment name"
                onChange={(e) => setSavingName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") doSaveSegment(); if (e.key === "Escape") setSavingName(null); }}
                className="h-8 w-44 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
              <Button size="sm" onClick={doSaveSegment} disabled={!savingName.trim() || saveSeg.isPending}>{saveSeg.isPending ? "Saving…" : "Save"}</Button>
              <button type="button" onClick={() => setSavingName(null)} className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-700">Cancel</button>
            </div>
          )}
          {loadedId && (
            <button type="button" onClick={() => delSeg.mutate(loadedId, { onSuccess: () => setLoadedId(null) })}
              className="ml-auto rounded-md px-2 py-1 text-xs font-medium text-slate-400 transition-colors hover:text-red-600">
              Delete this segment
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <Field label="View"><StyledSelect value={viewMode} onChange={(v) => setViewMode(v as "summary" | "list")} options={[["summary", "Summary"], ["list", "Member list"]]} /></Field>
          <Field label="Group by"><StyledSelect value={groupBy} onChange={(v) => setGroupBy(v as GroupKey)} options={GROUPS} /></Field>
          <Field label="Measure"><StyledSelect value={agg} onChange={(v) => setAgg(v as AggKey)} options={AGGS} /></Field>
          <div className="ml-auto"><Button variant="secondary" size="sm" onClick={exportCsv} disabled={rows.length === 0}><Download className="h-4 w-4" /> Export CSV</Button></div>
        </div>

        {/* Filter builder */}
        <div className="mt-4 border-t border-hairline pt-3">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Filters</div>
          <div className="space-y-2">
            {filters.map((f) => {
              const ft = fieldType(f.field);
              const ff = FILTER_FIELDS.find((x) => x.key === f.field)!;
              return (
                <div key={f.id} className="flex flex-wrap items-center gap-2">
                  <StyledSelect
                    value={f.field}
                    onChange={(v) => updateFilter(f.id, { field: v, op: opsFor(fieldType(v))[0][0], value: "" })}
                    options={FILTER_FIELDS.map((x) => [x.key, x.label])}
                  />
                  {opsFor(ft).length > 1 && (
                    <StyledSelect value={f.op} onChange={(v) => updateFilter(f.id, { op: v })} options={opsFor(ft)} />
                  )}
                  {ft === "enum" ? (
                    <StyledSelect
                      value={f.value}
                      onChange={(v) => updateFilter(f.id, { value: v })}
                      options={[["", "Any"], ...(f.field === "zone" ? zoneOptions : (ff.options ?? [])).map((o) => [o, o] as [string, string])]}
                    />
                  ) : ft === "number" ? (
                    <input type="number" value={f.value} placeholder="0"
                      onChange={(e) => updateFilter(f.id, { value: e.target.value })}
                      className="h-9 w-32 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                  ) : (
                    <input type="date" value={f.value}
                      onChange={(e) => updateFilter(f.id, { value: e.target.value })}
                      className="h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                  )}
                  <button type="button" onClick={() => removeFilter(f.id)} aria-label="Remove filter"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
            <button type="button" onClick={addFilter}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50 hover:text-brand-700">
              <Plus className="h-3.5 w-3.5" /> Add filter
            </button>
          </div>
        </div>
      </Card>

      {/* Summary */}
      <div className="mb-4 text-sm text-slate-600">
        {!isLoading && !isError && (
          <>
            <span className="text-2xl font-bold tabular-nums text-slate-900">{matched.toLocaleString()}</span>
            <span className="ml-2">agreements · {money(totalValue)} total value · {rows.length} {groupLabel.toLowerCase()} groups</span>
            <InfoTip className="ml-2" label="About this report">Every agreement, active and past. Build a view, save it as a segment to reuse it, or export it.</InfoTip>
          </>
        )}
      </div>

      {isError ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-600">Couldn't load agreements. Check the connection and try again.</p></Card>
      ) : isLoading ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-500">Loading agreements…</p></Card>
      ) : (viewMode === "list" ? members.length === 0 : rows.length === 0) ? (
        <Card className="p-10 text-center"><ShieldCheck className="mx-auto h-10 w-10 text-slate-300" /><h3 className="mt-3 text-[15px] font-bold tracking-tight text-slate-900">No agreements match these filters</h3></Card>
      ) : viewMode === "list" ? (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hairline bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Plan</th>
                <th className="px-4 py-2.5">Zone</th>
                <th className="px-4 py-2.5 text-right">Value</th>
                <th className="px-4 py-2.5 text-right">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {members.slice(0, 300).map((m) => (
                <tr key={`${m.custno}-${m.agrmt_no}`} role="button" tabIndex={0}
                  onClick={() => navigate(`/customers/${m.custno}?tab=agreements&from=reports`)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/customers/${m.custno}?tab=agreements&from=reports`); } }}
                  className="cursor-pointer transition-colors hover:bg-brand-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500/40">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{m.customer}</td>
                  <td className="px-4 py-2.5 text-slate-600">{m.status}</td>
                  <td className="px-4 py-2.5 text-slate-600">{m.plan}</td>
                  <td className="px-4 py-2.5 text-slate-600">{m.zone}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{money(m.value)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{m.expires ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {members.length > 300 && (
            <div className="border-t border-hairline px-4 py-2 text-xs text-slate-500">Showing first 300 of {members.length.toLocaleString()} — narrow with filters, or export for the full list.</div>
          )}
        </Card>
      ) : (
        <>
          <Card className="mb-4 p-5">
            <h3 className="mb-4 text-[15px] font-bold tracking-tight text-slate-900">{aggLabel} by {groupLabel.toLowerCase()}</h3>
            <div className="space-y-2.5">
              {rows.map((g, i) => (
                <div key={g.key} className="flex items-center gap-3">
                  <div className="w-36 flex-shrink-0 truncate text-right text-xs font-medium text-slate-600" title={g.label}>{g.label}</div>
                  <div className="relative h-6 flex-1 overflow-hidden rounded-md bg-slate-100">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${max > 0 ? (g.metric / max) * 100 : 0}%` }} transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: Math.min(i * 0.02, 0.25) }} className="h-full rounded-md bg-brand-500" />
                  </div>
                  <div className="w-28 flex-shrink-0 text-right text-xs font-semibold tabular-nums text-slate-900">{fmtMetric(g.metric)}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">{groupLabel}</th>
                  <th className="px-4 py-2.5 text-right">Count</th>
                  <th className="px-4 py-2.5 text-right">Total value</th>
                  <th className="px-4 py-2.5 text-right">Avg value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((g) => (
                  <tr key={g.key} className="transition-colors hover:bg-brand-50/40">
                    <td className="px-4 py-2.5 font-medium text-slate-800">{g.label}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{g.count.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{money(g.sum)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{money(g.count ? g.sum / g.count : 0)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-hairline bg-slate-50/60 font-semibold">
                  <td className="px-4 py-2.5 text-slate-900">Total</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{matched.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{money(totalValue)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{money(matched ? totalValue / matched : 0)}</td>
                </tr>
              </tfoot>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
